/**
 * ZAXIA TV Frontend - Main App Component
 * Three-zone layout with keyboard navigation
 * Phase 2B: Service Details, Booking Calendar, Hotel Bill
 * Phase 3A: Backend integration via DataProvider
 * Phase 4A: Smart Concierge with AskZaxiaView
 * Phase D4.2: Scheduled Green Stay preferences
 * Phase D4.5.1: Global Voice Overlay (voice is now system-level, not a page)
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  UsefulInfoSidebar,
  ConciergeGrid,
  TVSection,
  BookingConfirmationModal,
  type BookingModalVoiceCmd,
  BookingDetailModal,
  HotelBillModal,
  TVFullscreenOverlay,
  GlobalVoiceOverlay,
  DemoModeIndicator,
  ArrivalExperience,
  RequestStatusNotification,
  GreenStaySavingsFeedback,
  TVSafeAreaLayout,
} from '@/components';
import { GuestConsentBanner, getStoredConsent } from '@/components/GuestConsentBanner';
import type { ConciergeView } from '@/components/ConciergeGrid';
import type { CreateBookingRequest } from '@/api';
import { api } from '@/api';
import { useZoneNavigation, useIsTV } from '@/hooks';
import { useData } from '@/providers/DataProvider';
import { useDemoMode } from '@/providers/DemoModeProvider';
import { useGuestRequestNotifications } from '@/hooks/useGuestRequestNotifications';
import { getServiceDetailById } from '@/data/mockHotelData';
import { ServiceCategory } from '@/types';
import type { ServiceDetail, Booking, ServiceOption } from '@/types';
import type { EcoSavings } from '@/services/greenStay';
import { calculateStaySavings, validateGreenStayState } from '@/services/greenStay';

// Phase D5.1: App flow gating type
type AppFlow = 'arrival' | 'main';

export default function App() {
  // Phase 3A: Get data from backend via DataProvider
  const {
    hotel,
    guest,
    room,
    stay,
    onboardingCompleted,
    bookings,
    calendarEvents,
    bill,
    loading,
    error,
    addBooking,
    refreshBill,
    refreshBookings,
  } = useData();

  // Phase D4.5.5: Demo Mode
  const { isDemoMode } = useDemoMode();

  // PILOT-3: Guest consent gate — validate stayId so a new guest on the same
  // device (kiosk/TV) is not auto-passed through on a previous guest's consent.
  const [consented, setConsented] = useState(() => {
    const stored = getStoredConsent();
    return stored !== null && stored.stayId === stay?.stayId;
  });

  // Phase D5.1: App flow gating (arrival → main)
  // Phase P2.4-5: Skip arrival if onboarding already completed for this stay
  const [appFlow, setAppFlow] = useState<AppFlow>(() => {
    if (onboardingCompleted) {
      console.log('[ONBOARDING] Skipped (already completed for this stay)');
      return 'main';
    }
    return 'arrival';
  });

  // PILOT-3: Re-check consent if stay loads asynchronously after first render
  useEffect(() => {
    if (stay && !consented) {
      const stored = getStoredConsent();
      if (stored?.stayId === stay.stayId) setConsented(true);
    }
  }, [stay, consented]);

  // Phase P2.4-5: Update appFlow if onboardingCompleted changes (e.g., after reload)
  useEffect(() => {
    if (onboardingCompleted && appFlow === 'arrival') {
      console.log('[ONBOARDING] Skipped (already completed - detected after load)');
      setAppFlow('main');
    }
  }, [onboardingCompleted, appFlow]);

  // Phase D5.1: Handler for when arrival flow completes
  // Phase P2.4-5: Mark onboarding complete in PMS before transitioning
  const handleArrivalComplete = useCallback(async () => {
    console.log('[ZAXIA] Arrival flow complete, marking onboarding complete...');
    try {
      await api.completeOnboarding();
      console.log('[ONBOARDING] ✓ Completed and persisted to PMS');
    } catch (err) {
      console.error('[ONBOARDING] Failed to persist completion:', err);
      // Continue to main app even if API call fails (graceful degradation)
    }
    setAppFlow('main');
  }, []);

  // Zone navigation
  const { activeZone, setActiveZone, isZoneActive } = useZoneNavigation('main');

  // P2.8-3: TV layout guard — apply safe-area padding on large viewports
  const isTV = useIsTV();

  // Phase D1.2: Zone switching voice command handlers
  const handleSwitchToSidebar = useCallback(() => {
    setActiveZone('sidebar');
  }, [setActiveZone]);

  const handleSwitchToConcierge = useCallback(() => {
    setActiveZone('main');
  }, [setActiveZone]);

  const handleSwitchToTv = useCallback(() => {
    setActiveZone('tv');
  }, [setActiveZone]);

  // P2.8-4b: D-Pad zone boundary callbacks — route focus between zones
  const handleMainGridLeftBoundary = useCallback(() => setActiveZone('sidebar'), [setActiveZone]);
  const handleMainGridRightBoundary = useCallback(() => setActiveZone('tv'), [setActiveZone]);
  const handleSidebarRightBoundary = useCallback(() => setActiveZone('main'), [setActiveZone]);

  // Phase D4.2: Green Stay preferences - scheduled days
  const [greenStayEnabled, setGreenStayEnabled] = useState(false);
  const [skipCleaningDays, setSkipCleaningDays] = useState<string[]>([]);
  const [towelsOnlyDays, setTowelsOnlyDays] = useState<string[]>([]);
  const [showGreenStayToast, setShowGreenStayToast] = useState(false);
  const [greenStayToastMessage, setGreenStayToastMessage] = useState('');

  // Phase P2.2-10: Eco savings feedback
  const [showSavingsFeedback, setShowSavingsFeedback] = useState(false);
  const [currentSavings, setCurrentSavings] = useState<EcoSavings>({ water: 0, laundry: 0, energy: 0, co2: 0 });

  // Phase D4.2: Persist green preferences to backend (MUST be defined before useEffect)
  const persistGreenPreferences = useCallback(async (updates: {
    greenStayOptIn?: boolean;
    skipCleaningDates?: string[];
    towelsOnlyDates?: string[];
  }) => {
    try {
      await api.updateGreenPreferences(updates);
      console.log('[ZAXIA] Green preferences persisted:', updates);
    } catch (err) {
      console.error('[ZAXIA] Failed to persist green preferences:', err);
    }
  }, []);

  // Initialize green preferences from stay data
  useEffect(() => {
    if (stay?.greenPreferences) {
      setGreenStayEnabled(stay.greenPreferences.greenStayOptIn);

      // Phase P2.2-10: Validate and auto-correct state
      const validated = validateGreenStayState({
        skipCleaningDays: stay.greenPreferences.skipCleaningDates || [],
        towelsOnlyDays: stay.greenPreferences.towelsOnlyDates || [],
      });

      setSkipCleaningDays(validated.skipCleaningDays);
      setTowelsOnlyDays(validated.towelsOnlyDays);

      if (validated.corrected) {
        console.warn('[ZAXIA] Green Stay state auto-corrected (duplicates removed)');
        // Persist corrected state
        persistGreenPreferences({
          skipCleaningDates: validated.skipCleaningDays,
          towelsOnlyDates: validated.towelsOnlyDays,
        });
      }
    }
  }, [stay, persistGreenPreferences]);

  // TV state
  const [tvFullscreen, setTvFullscreen] = useState(false);
  const [tvMuted, setTvMuted] = useState(true); // Phase D2.2: Default muted
  const [isTvOn, setIsTvOn] = useState(true); // Phase D2.2: TV ON by default

  // Phase 2B: Service Detail state
  const [selectedService, setSelectedService] = useState<ServiceDetail | null>(null);

  // Phase 2B: Booking flow state
  const [bookingService, setBookingService] = useState<ServiceDetail | null>(null);
  const [bookingOption, setBookingOption] = useState<ServiceOption | null>(null);

  // Phase 2B: Calendar state
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  // Phase 2B: Bill state
  const [showBill, setShowBill] = useState(false);

  // Phase 2B: Concierge panel view state (weather, messages placeholders)
  const [conciergeView, setConciergeView] = useState<ConciergeView>('grid');

  // Phase D4.5.1: Global Voice Overlay state
  const [voiceOverlayActive, setVoiceOverlayActive] = useState(false);

  // Phase D4.5.2: Restaurant initial slots from voice (for "book me a table for X at Y")
  const [restaurantInitialSlots, setRestaurantInitialSlots] = useState<{
    time?: string;
    partySize?: number;
    transcript?: string;
  } | undefined>(undefined);

  // P2.7-2: Pre-selected option for generic service detail pages (Bar/Pool/Gym/Transport)
  const [serviceInitialOption, setServiceInitialOption] = useState<string | undefined>(undefined);

  // P2.7-2: Booking modal voice command state
  const bookingModalVoiceCmdIdRef = useRef(0);
  const [bookingModalVoiceCmd, setBookingModalVoiceCmd] = useState<BookingModalVoiceCmd | null>(null);

  // Disable global spacebar when Room Service, Spa, Restaurant, Explore, or
  // any StandardServiceDetailPage service (Bar, Activity, Transport) is active.
  // Those components register their own Space key capture handler.
  const isRoomServiceActive = selectedService?.category === ServiceCategory.ROOM_SERVICE;
  const isSpaActive = selectedService?.category === ServiceCategory.SPA;
  const isRestaurantActive = selectedService?.category === ServiceCategory.RESTAURANT;
  const isExploreActive = conciergeView === 'explore';
  const isStandardServiceActive =
    selectedService?.category === ServiceCategory.BAR ||
    selectedService?.category === ServiceCategory.ACTIVITY ||
    selectedService?.category === ServiceCategory.TRANSPORT;
  const isLocalVoiceHandlingActive =
    isRoomServiceActive || isSpaActive || isRestaurantActive || isExploreActive || isStandardServiceActive;

  // Phase D4.5.1: Global spacebar listener to open voice overlay
  useEffect(() => {
    if (isLocalVoiceHandlingActive) return; // Don't interfere with local voice handling
    if (voiceOverlayActive) return; // Overlay handles its own spacebar

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat) return;
      const tag = document.activeElement?.tagName;
      // Only block in text inputs - allow Space to trigger voice from buttons (e.g., calendar navigation)
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      e.preventDefault();
      e.stopPropagation();
      setVoiceOverlayActive(true);
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isLocalVoiceHandlingActive, voiceOverlayActive]);

  // P2.8-4b: TV zone — ArrowLeft returns focus to main grid
  useEffect(() => {
    if (activeZone !== 'tv') return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); setActiveZone('main'); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeZone, setActiveZone]);

  // Phase P2.2-6: Guest request status notifications
  const { notification, dismissNotification } = useGuestRequestNotifications(tvFullscreen);

  // Handlers
  const handleServiceSelect = useCallback((serviceId: string) => {
    console.log('[ZAXIA] Service selected:', serviceId);

    // Phase D3.1: Explore card opens explore view directly
    if (serviceId === 'card_explore') {
      setConciergeView('explore');
      return;
    }

    const serviceDetail = getServiceDetailById(serviceId);
    if (serviceDetail) {
      setSelectedService(serviceDetail);
    }
  }, []);

  const handleServiceBack = useCallback(() => {
    setSelectedService(null);
    // Phase D4.5.2: Clear restaurant initial slots when leaving restaurant page
    setRestaurantInitialSlots(undefined);
    // P2.7-2: Clear service initial option when leaving service detail page
    setServiceInitialOption(undefined);
  }, []);

  const handleOptionSelect = useCallback((option: ServiceOption) => {
    console.log('[ZAXIA] Option selected:', option.name);
    if (selectedService) {
      setBookingService(selectedService);
      setBookingOption(option);
    }
  }, [selectedService]);

  // Phase 3A: Updated to persist booking via API
  const handleBookingConfirm = useCallback(async (booking: Booking) => {
    console.log('[ZAXIA] Booking confirmed:', booking);
    setBookingModalVoiceCmd(null); // clear any pending voice command

    try {
      // Create booking via API
      await addBooking({
        serviceId: booking.serviceId,
        serviceName: booking.serviceName,
        category: booking.category,
        datetime: booking.datetime,
        durationMinutes: booking.durationMinutes,
        price: booking.price,
        location: booking.location || undefined,
      });
      console.log('[ZAXIA] Booking persisted to backend');
      // Refresh bill to include new booking charge
      await refreshBill();
    } catch (e) {
      console.error('[ZAXIA] Failed to persist booking:', e);
    }

    setBookingService(null);
    setBookingOption(null);
    setSelectedService(null); // Return to main grid
  }, [addBooking, refreshBill]);

  const handleBookingCancel = useCallback(() => {
    setBookingModalVoiceCmd(null);
    setBookingService(null);
    setBookingOption(null);
  }, []);

  const handleChannelSelect = useCallback((channelId: string) => {
    console.log('[ZAXIA] Channel selected:', channelId);
    // Phase D2.2: Map channel ID to index for video switching
    const channelMap: Record<string, number> = {
      'ch_hbo': 0, 'ch_cnn': 1, 'ch_bbc': 2, 'ch_espn': 3, 'ch_discovery': 4, 'ch_streaming': 5
    };
    const index = channelMap[channelId];
    if (index !== undefined) {
      setCurrentChannelIndex(index);
    }
  }, []);

  const handleBillClick = useCallback(() => {
    console.log('[ZAXIA] Bill requested');
    setShowBill(true);
  }, []);

  const handleCalendarClick = useCallback(() => {
    console.log('[ZAXIA] Calendar requested');
    setConciergeView('calendar');
  }, []);

  const handleBookingSelect = useCallback((booking: Booking) => {
    console.log('[ZAXIA] Booking selected:', booking.serviceName);
    setSelectedBooking(booking);
  }, []);

  const handleMessagesClick = useCallback(() => {
    console.log('[ZAXIA] Messages requested');
    setConciergeView('messages');
  }, []);

  const handleRequestsClick = useCallback(() => {
    console.log('[ZAXIA] My Requests requested');
    setConciergeView('requests');
  }, []);

  // Phase P2.2-10: Handle Green Stay view navigation
  const handleGreenStayClick = useCallback(() => {
    console.log('[ZAXIA] Green Stay requested');
    setConciergeView('green-stay');
  }, []);

  // Phase P2.2-9: Store the requests voice command handler from ConciergeGrid
  const [requestsVoiceCommandHandler, setRequestsVoiceCommandHandler] = useState<((command: any) => string | void) | null>(null);

  const handleRequestsVoiceCommandReady = useCallback((handler: (command: any) => string | void) => {
    setRequestsVoiceCommandHandler(() => handler);
  }, []);

  // Phase P2.2-10: Store the green stay voice command handler from ConciergeGrid
  const [greenStayVoiceCommandHandler, setGreenStayVoiceCommandHandler] = useState<((command: any) => string) | null>(null);

  const handleGreenStayVoiceCommandReady = useCallback((handler: (command: any) => string) => {
    setGreenStayVoiceCommandHandler(() => handler);
  }, []);

  // P2.9-1: Messages view synchronous command handler (registered by MessagesPlaceholderView)
  const [messagesVoiceCommandHandler, setMessagesVoiceCommandHandler] = useState<((transcript: string) => string) | null>(null);
  const handleMessagesVoiceCommandReady = useCallback((handler: (transcript: string) => string) => {
    setMessagesVoiceCommandHandler(() => handler);
  }, []);

  const handleWeatherClick = useCallback(() => {
    console.log('[ZAXIA] Weather requested');
    setConciergeView('weather');
  }, []);

  const handleViewClose = useCallback(() => {
    console.log('[ZAXIA] View closed');
    setIsCancelConfirmShowing(false); // Clear any pending confirmation dialog
    setConciergeView('grid');
  }, []);

  // Phase D4.5.3: Calendar initial date (for voice navigation)
  const [calendarInitialDate, setCalendarInitialDate] = useState<string | undefined>();

  // Phase D4.5.3: Voice-initiated cancellation state
  const [pendingCancelBookingId, setPendingCancelBookingId] = useState<string | undefined>();

  // Voice-initiated selection state
  const [pendingSelectBookingId, setPendingSelectBookingId] = useState<string | undefined>();

  // Phase D4.5.3: Track if cancel-confirm dialog is showing (for voice recognition)
  const [isCancelConfirmShowing, setIsCancelConfirmShowing] = useState(false);

  // Phase D4.5.3: Voice confirmation of cancellation
  const [voiceConfirmCancel, setVoiceConfirmCancel] = useState(false);
  const [voiceKeepBooking, setVoiceKeepBooking] = useState(false);

  // Phase D4.5.4: Modify booking state
  const [pendingModifyBookingId, setPendingModifyBookingId] = useState<string | undefined>();
  const [pendingModifyChanges, setPendingModifyChanges] = useState<{
    newTime?: string;
    newDay?: string;
    newPartySize?: number;
  } | undefined>();
  const [isModifyConfirmShowing, setIsModifyConfirmShowing] = useState(false);
  const [voiceConfirmModify, setVoiceConfirmModify] = useState(false);
  const [voiceCancelModify, setVoiceCancelModify] = useState(false);

  // Voice booking list navigation
  const [calendarVoiceBookingNav, setCalendarVoiceBookingNav] = useState<'next' | 'prev' | undefined>();
  const [calendarVoiceDayNav, setCalendarVoiceDayNav] = useState<'next' | 'prev' | undefined>();

  // Phase 4A: Voice command handlers
  const handleVoiceOpenCalendar = useCallback(() => {
    console.log('[ZAXIA] Voice: Open calendar');
    setCalendarInitialDate(undefined); // Clear any specific date
    setPendingCancelBookingId(undefined); // Clear any pending cancellation
    setIsCancelConfirmShowing(false); // Clear any pending confirmation dialog
    setConciergeView('calendar');
  }, []);

  // Phase D4.5.3: Open calendar on specific day
  const handleVoiceOpenCalendarDay = useCallback((day: string) => {
    console.log('[ZAXIA] Voice: Open calendar on', day);
    setCalendarInitialDate(day);
    setConciergeView('calendar');
  }, []);

  // Phase D4.5.3: Calendar navigation callbacks (when calendar is already open)
  const handleCalendarNextDay = useCallback(() => {
    console.log('[ZAXIA] Voice: Calendar next day');
    setCalendarVoiceDayNav('next');
  }, []);

  const handleCalendarPrevDay = useCallback(() => {
    console.log('[ZAXIA] Voice: Calendar prev day');
    setCalendarVoiceDayNav('prev');
  }, []);

  const handleVoiceDayNavHandled = useCallback(() => {
    setCalendarVoiceDayNav(undefined);
  }, []);

  const handleCalendarNextBooking = useCallback(() => {
    console.log('[ZAXIA] Voice: Calendar next booking');
    setCalendarVoiceBookingNav('next');
  }, []);

  const handleCalendarPrevBooking = useCallback(() => {
    console.log('[ZAXIA] Voice: Calendar prev booking');
    setCalendarVoiceBookingNav('prev');
  }, []);

  const handleVoiceBookingNavHandled = useCallback(() => {
    setCalendarVoiceBookingNav(undefined);
  }, []);

  const handleCalendarGoToDay = useCallback((day: string) => {
    console.log('[ZAXIA] Voice: Calendar go to', day);
    setCalendarInitialDate(day);
  }, []);

  // Phase D4.5.3: Voice-initiated booking cancellation
  // This finds matching bookings and either:
  // - Opens calendar with the booking selected for cancellation (single match)
  // - Returns multiple matches for Zaxia to ask which one
  const handleCalendarCancelBooking = useCallback((bookingText?: string, dayRef?: string, timeRef?: string): { matchedBookings: typeof bookings; needsClarification: boolean } => {
    console.log('[ZAXIA] Voice: Cancel booking', bookingText, 'day:', dayRef, 'time:', timeRef);

    if (!bookings || bookings.length === 0) {
      return { matchedBookings: [], needsClarification: false };
    }

    const now = new Date();

    // Filter to only confirmed/pending bookings that are in the FUTURE
    const confirmedBookings = bookings.filter(b => {
      const isFuture = new Date(b.datetime) > now;
      const isActive = b.status === 'CONFIRMED' || b.status === 'PENDING';
      return isActive && isFuture;
    });

    if (confirmedBookings.length === 0) {
      return { matchedBookings: [], needsClarification: false };
    }

    // Parse day reference if provided
    let targetDate: string | null = null;
    if (dayRef) {
      const today = new Date();
      const ref = dayRef.toLowerCase().trim();

      if (ref === 'today') {
        targetDate = today.toISOString().split('T')[0]!;
      } else if (ref === 'tomorrow') {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        targetDate = tomorrow.toISOString().split('T')[0]!;
      } else {
        // Try to match day names
        const dayMap: Record<string, number> = {
          sunday: 0, sun: 0, monday: 1, mon: 1, tuesday: 2, tue: 2, tues: 2,
          wednesday: 3, wed: 3, thursday: 4, thu: 4, thurs: 4,
          friday: 5, fri: 5, saturday: 6, sat: 6,
        };
        if (ref in dayMap) {
          const targetDay = dayMap[ref]!;
          const currentDay = today.getDay();
          let daysAhead = targetDay - currentDay;
          if (daysAhead <= 0) daysAhead += 7;
          const targetDateObj = new Date(today);
          targetDateObj.setDate(today.getDate() + daysAhead);
          targetDate = targetDateObj.toISOString().split('T')[0]!;
        }
      }
    }

    // Parse time reference if provided (format: "HH:MM" in 24-hour)
    let targetHour: number | null = null;
    let targetMinute: number | null = null;
    if (timeRef) {
      const timeParts = timeRef.split(':');
      if (timeParts.length >= 2) {
        targetHour = parseInt(timeParts[0]!, 10);
        targetMinute = parseInt(timeParts[1]!, 10);
        console.log('[ZAXIA] Parsed time reference:', targetHour, ':', targetMinute);
      }
    }

    // Match bookings by type/category and optionally day
    let matchedBookings = confirmedBookings;

    // Filter by booking type/name if provided
    // Special case: "booking" alone means "any booking", not a filter
    if (bookingText && bookingText.toLowerCase() !== 'booking') {
      // Normalize common misspellings
      let searchText = bookingText.toLowerCase().trim();
      searchText = searchText.replace(/^tara$/i, 'terra'); // Fix "tara" → "terra"

      matchedBookings = matchedBookings.filter(b => {
        const nameMatch = b.serviceName.toLowerCase().includes(searchText);
        const categoryMatch = b.category.toLowerCase().includes(searchText);
        // Also match common aliases and venue names
        const aliases: Record<string, string[]> = {
          'spa': ['massage', 'wellness', 'treatment', 'facial'],
          'restaurant': ['dinner', 'lunch', 'breakfast', 'meal', 'table', 'terra', 'terra restaurant', 'tara'],
          'activity': ['tour', 'excursion', 'museum', 'visit', 'maat'],
          'transport': ['taxi', 'transfer', 'car', 'shuttle'],
          'room_service': ['room service', 'room-service', 'roomservice'],
        };
        const aliasMatch = Object.entries(aliases).some(([category, keywords]) => {
          if (keywords.some(kw => searchText.includes(kw))) {
            return b.category.toLowerCase() === category || b.serviceName.toLowerCase().includes(category);
          }
          if (searchText === category || searchText.replace(/[_\s-]/g, '') === category.replace(/[_\s-]/g, '')) {
            return b.category.toLowerCase() === category;
          }
          return false;
        });
        return nameMatch || categoryMatch || aliasMatch;
      });
    }

    // Filter by day if provided
    if (targetDate) {
      matchedBookings = matchedBookings.filter(b => {
        const bookingDate = new Date(b.datetime).toISOString().split('T')[0];
        return bookingDate === targetDate;
      });
    }

    // Filter by time if provided (allow ±30 minute tolerance)
    if (targetHour !== null) {
      matchedBookings = matchedBookings.filter(b => {
        const bookingDateTime = new Date(b.datetime);
        const bookingHour = bookingDateTime.getHours();
        const bookingMinute = bookingDateTime.getMinutes();

        // Convert both to minutes since midnight for easier comparison
        const targetMinutes = targetHour * 60 + (targetMinute ?? 0);
        const bookingMinutes = bookingHour * 60 + bookingMinute;

        // Allow 30 minute tolerance
        const tolerance = 30;
        const diff = Math.abs(targetMinutes - bookingMinutes);
        const matches = diff <= tolerance;

        console.log('[ZAXIA] Time comparison:', b.serviceName,
          'booking:', bookingHour + ':' + bookingMinute,
          'target:', targetHour + ':' + (targetMinute ?? 0),
          'diff:', diff, 'matches:', matches);

        return matches;
      });
    }

    console.log('[ZAXIA] Matched bookings for cancellation:', matchedBookings.length);

    // If exactly one match, select it for cancellation
    if (matchedBookings.length === 1) {
      const booking = matchedBookings[0]!;
      const bookingDate = new Date(booking.datetime).toISOString().split('T')[0]!;

      // Open calendar on the booking's date with it pre-selected for cancellation
      setCalendarInitialDate(bookingDate);
      setPendingCancelBookingId(booking.bookingId);
      setConciergeView('calendar');

      return { matchedBookings, needsClarification: false };
    }

    // If multiple matches, return them for clarification
    if (matchedBookings.length > 1) {
      return { matchedBookings, needsClarification: true };
    }

    // No matches
    return { matchedBookings: [], needsClarification: false };
  }, [bookings]);

  // Voice-initiated booking selection - select a booking to view details
  const handleCalendarSelectBooking = useCallback((bookingText: string, dayRef?: string, bookingIndex?: number): { matchedBookings: typeof bookings; needsClarification: boolean } => {
    console.log('[ZAXIA] Voice: Select booking', bookingText, 'day:', dayRef, 'index:', bookingIndex);

    if (!bookings || bookings.length === 0) {
      return { matchedBookings: [], needsClarification: false };
    }

    const now = new Date();

    // Filter to only confirmed/pending bookings that are in the FUTURE
    const confirmedBookings = bookings.filter(b => {
      const isFuture = new Date(b.datetime) > now;
      const isActive = b.status === 'CONFIRMED' || b.status === 'PENDING';
      return isActive && isFuture;
    });

    if (confirmedBookings.length === 0) {
      return { matchedBookings: [], needsClarification: false };
    }

    // Handle ordinal index selection ("first booking", "second booking", etc.)
    if (bookingIndex !== undefined) {
      const sorted = [...confirmedBookings].sort((a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime());
      const target = bookingIndex === -1 ? sorted[sorted.length - 1] : sorted[bookingIndex];
      if (!target) return { matchedBookings: [], needsClarification: false };
      const bookingDate = new Date(target.datetime).toISOString().split('T')[0]!;
      setCalendarInitialDate(bookingDate);
      setPendingSelectBookingId(target.bookingId);
      setConciergeView('calendar');
      return { matchedBookings: [target], needsClarification: false };
    }

    // Parse day reference if provided
    let targetDate: string | null = null;
    if (dayRef) {
      const today = new Date();
      const ref = dayRef.toLowerCase().trim();

      if (ref === 'today') {
        targetDate = today.toISOString().split('T')[0]!;
      } else if (ref === 'tomorrow') {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        targetDate = tomorrow.toISOString().split('T')[0]!;
      } else {
        const dayMap: Record<string, number> = {
          sunday: 0, sun: 0, monday: 1, mon: 1, tuesday: 2, tue: 2, tues: 2,
          wednesday: 3, wed: 3, thursday: 4, thu: 4, thurs: 4,
          friday: 5, fri: 5, saturday: 6, sat: 6,
        };
        if (ref in dayMap) {
          const targetDay = dayMap[ref]!;
          const currentDay = today.getDay();
          let daysAhead = targetDay - currentDay;
          if (daysAhead <= 0) daysAhead += 7;
          const targetDateObj = new Date(today);
          targetDateObj.setDate(today.getDate() + daysAhead);
          targetDate = targetDateObj.toISOString().split('T')[0]!;
        }
      }
    }

    // Match bookings by type/category and optionally day
    let matchedBookings = confirmedBookings;

    // Filter by booking type/name if provided
    if (bookingText && bookingText.toLowerCase() !== 'booking') {
      let searchText = bookingText.toLowerCase().trim();
      searchText = searchText.replace(/^tara$/i, 'terra');

      matchedBookings = matchedBookings.filter(b => {
        const nameMatch = b.serviceName.toLowerCase().includes(searchText);
        const categoryMatch = b.category.toLowerCase().includes(searchText);
        const aliases: Record<string, string[]> = {
          'spa': ['massage', 'wellness', 'treatment', 'facial'],
          'restaurant': ['dinner', 'lunch', 'breakfast', 'meal', 'table', 'terra', 'terra restaurant', 'tara'],
          'activity': ['tour', 'excursion', 'museum', 'visit', 'maat'],
          'transport': ['taxi', 'transfer', 'car', 'shuttle'],
          'room_service': ['room service', 'room-service', 'roomservice'],
        };
        const aliasMatch = Object.entries(aliases).some(([category, keywords]) => {
          if (keywords.some(kw => searchText.includes(kw))) {
            return b.category.toLowerCase() === category || b.serviceName.toLowerCase().includes(category);
          }
          if (searchText === category || searchText.replace(/[_\s-]/g, '') === category.replace(/[_\s-]/g, '')) {
            return b.category.toLowerCase() === category;
          }
          return false;
        });
        return nameMatch || categoryMatch || aliasMatch;
      });
    }

    // Filter by day if provided
    if (targetDate) {
      matchedBookings = matchedBookings.filter(b => {
        const bookingDate = new Date(b.datetime).toISOString().split('T')[0];
        return bookingDate === targetDate;
      });
    }

    console.log('[ZAXIA] Matched bookings for selection:', matchedBookings.length);

    // If exactly one match, select it for viewing
    if (matchedBookings.length === 1) {
      const booking = matchedBookings[0]!;
      const bookingDate = new Date(booking.datetime).toISOString().split('T')[0]!;

      // Open calendar on the booking's date with it pre-selected for viewing
      setCalendarInitialDate(bookingDate);
      setPendingSelectBookingId(booking.bookingId);
      setConciergeView('calendar');

      return { matchedBookings, needsClarification: false };
    }

    // If multiple matches, return them for clarification
    if (matchedBookings.length > 1) {
      return { matchedBookings, needsClarification: true };
    }

    // No matches
    return { matchedBookings: [], needsClarification: false };
  }, [bookings]);

  // Handler for when pending select has been processed by the calendar
  const handlePendingSelectHandled = useCallback(() => {
    setPendingSelectBookingId(undefined);
  }, []);

  // Handler for when pending cancel has been processed by the calendar
  // This means the cancel-confirm dialog is now showing
  const handlePendingCancelHandled = useCallback(() => {
    setPendingCancelBookingId(undefined);
    setIsCancelConfirmShowing(true); // Dialog is now showing
  }, []);

  // Phase D4.5.3: Voice confirmation of cancellation
  const handleVoiceConfirmCancel = useCallback(() => {
    console.log('[ZAXIA] Voice: Confirm cancel');
    setVoiceConfirmCancel(true);
  }, []);

  const handleVoiceKeepBooking = useCallback(() => {
    console.log('[ZAXIA] Voice: Keep booking (cancel dismissed)');
    setVoiceKeepBooking(true);
  }, []);

  // ============================================================================
  // Phase D4.5.4: Voice-initiated booking modification
  // ============================================================================

  const handleCalendarModifyBooking = useCallback((
    bookingText?: string,
    dayRef?: string,
    timeRef?: string,
    newTime?: string,
    newDay?: string,
    newPartySize?: number
  ): { matchedBookings: typeof bookings; needsClarification: boolean } => {
    console.log('[ZAXIA] Voice: Modify booking', bookingText, 'day:', dayRef, 'time:', timeRef, 'newTime:', newTime, 'newDay:', newDay, 'newPartySize:', newPartySize);

    if (!bookings || bookings.length === 0) {
      return { matchedBookings: [], needsClarification: false };
    }

    const now = new Date();

    // Filter to only confirmed/pending bookings that are in the FUTURE
    const futureBookings = bookings.filter(b => {
      const isFuture = new Date(b.datetime) > now;
      const isActive = b.status === 'CONFIRMED' || b.status === 'PENDING';
      return isActive && isFuture;
    });

    if (futureBookings.length === 0) {
      return { matchedBookings: [], needsClarification: false };
    }

    // Parse day reference if provided (for identifying WHICH booking)
    let targetDate: string | null = null;
    if (dayRef) {
      const today = new Date();
      const ref = dayRef.toLowerCase().trim();

      if (ref === 'today') {
        targetDate = today.toISOString().split('T')[0]!;
      } else if (ref === 'tomorrow') {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        targetDate = tomorrow.toISOString().split('T')[0]!;
      } else {
        const dayMap: Record<string, number> = {
          sunday: 0, sun: 0, monday: 1, mon: 1, tuesday: 2, tue: 2, tues: 2,
          wednesday: 3, wed: 3, thursday: 4, thu: 4, thurs: 4,
          friday: 5, fri: 5, saturday: 6, sat: 6,
        };
        if (ref in dayMap) {
          const targetDay = dayMap[ref]!;
          const currentDay = today.getDay();
          let daysAhead = targetDay - currentDay;
          if (daysAhead <= 0) daysAhead += 7;
          const targetDateObj = new Date(today);
          targetDateObj.setDate(today.getDate() + daysAhead);
          targetDate = targetDateObj.toISOString().split('T')[0]!;
        }
      }
    }

    // Parse time reference if provided (for identifying WHICH booking)
    let targetHour: number | null = null;
    let targetMinute: number | null = null;
    if (timeRef) {
      const timeParts = timeRef.split(':');
      if (timeParts.length >= 2) {
        targetHour = parseInt(timeParts[0]!, 10);
        targetMinute = parseInt(timeParts[1]!, 10);
      }
    }

    // Match bookings by type/category and optionally day/time
    let matchedBookings = futureBookings;

    // Filter by booking type/name if provided
    if (bookingText && bookingText.toLowerCase() !== 'booking') {
      let searchText = bookingText.toLowerCase().trim();
      searchText = searchText.replace(/^tara$/i, 'terra');

      matchedBookings = matchedBookings.filter(b => {
        const nameMatch = b.serviceName.toLowerCase().includes(searchText);
        const categoryMatch = b.category.toLowerCase().includes(searchText);
        const aliases: Record<string, string[]> = {
          'spa': ['massage', 'wellness', 'treatment', 'facial'],
          'restaurant': ['dinner', 'lunch', 'breakfast', 'meal', 'table', 'terra', 'terra restaurant', 'tara'],
          'activity': ['tour', 'excursion', 'museum', 'visit', 'maat'],
          'transport': ['taxi', 'transfer', 'car', 'shuttle'],
          'room_service': ['room service', 'room-service', 'roomservice'],
        };
        const aliasMatch = Object.entries(aliases).some(([category, keywords]) => {
          if (keywords.some(kw => searchText.includes(kw))) {
            return b.category.toLowerCase() === category || b.serviceName.toLowerCase().includes(category);
          }
          if (searchText === category || searchText.replace(/[_\s-]/g, '') === category.replace(/[_\s-]/g, '')) {
            return b.category.toLowerCase() === category;
          }
          return false;
        });
        return nameMatch || categoryMatch || aliasMatch;
      });
    }

    // Filter by day if provided
    if (targetDate) {
      matchedBookings = matchedBookings.filter(b => {
        const bookingDate = new Date(b.datetime).toISOString().split('T')[0];
        return bookingDate === targetDate;
      });
    }

    // Filter by time if provided (±30 minute tolerance)
    if (targetHour !== null) {
      matchedBookings = matchedBookings.filter(b => {
        const bookingDateTime = new Date(b.datetime);
        const bookingHour = bookingDateTime.getHours();
        const bookingMinute = bookingDateTime.getMinutes();
        const targetMinutes = targetHour * 60 + (targetMinute ?? 0);
        const bookingMinutes = bookingHour * 60 + bookingMinute;
        const diff = Math.abs(targetMinutes - bookingMinutes);
        return diff <= 30;
      });
    }

    console.log('[ZAXIA] Matched bookings for modification:', matchedBookings.length);

    // If exactly one match, select it for modification
    if (matchedBookings.length === 1) {
      const booking = matchedBookings[0]!;
      const bookingDate = new Date(booking.datetime).toISOString().split('T')[0]!;

      // Convert newDay to YYYY-MM-DD format if it's a day reference
      let newDayDate: string | undefined;
      if (newDay) {
        const today = new Date();
        const ref = newDay.toLowerCase().trim();

        if (ref === 'today') {
          newDayDate = today.toISOString().split('T')[0]!;
        } else if (ref === 'tomorrow') {
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          newDayDate = tomorrow.toISOString().split('T')[0]!;
        } else {
          const dayMap: Record<string, number> = {
            sunday: 0, sun: 0, monday: 1, mon: 1, tuesday: 2, tue: 2, tues: 2,
            wednesday: 3, wed: 3, thursday: 4, thu: 4, thurs: 4,
            friday: 5, fri: 5, saturday: 6, sat: 6,
          };
          if (ref in dayMap) {
            const targetDay = dayMap[ref]!;
            const currentDay = today.getDay();
            let daysAhead = targetDay - currentDay;
            if (daysAhead <= 0) daysAhead += 7;
            const targetDateObj = new Date(today);
            targetDateObj.setDate(today.getDate() + daysAhead);
            newDayDate = targetDateObj.toISOString().split('T')[0]!;
          }
        }
      }

      // Store pending modification
      setPendingModifyBookingId(booking.bookingId);
      setPendingModifyChanges({
        newTime,
        newDay: newDayDate,
        newPartySize,
      });

      // Open calendar on the booking's date
      setCalendarInitialDate(bookingDate);
      setConciergeView('calendar');

      return { matchedBookings, needsClarification: false };
    }

    // If multiple matches, return them for clarification
    if (matchedBookings.length > 1) {
      return { matchedBookings, needsClarification: true };
    }

    return { matchedBookings: [], needsClarification: false };
  }, [bookings]);

  // Handler for when pending modify has been processed by the calendar
  const handlePendingModifyHandled = useCallback(() => {
    console.log('[ZAXIA] handlePendingModifyHandled called - setting isModifyConfirmShowing=true');
    setPendingModifyBookingId(undefined);
    setIsModifyConfirmShowing(true);
  }, []);

  // Phase D4.5.4: Voice confirmation of modification
  const handleVoiceConfirmModify = useCallback(() => {
    console.log('[ZAXIA] Voice: Confirm modify');
    setVoiceConfirmModify(true);
  }, []);

  const handleVoiceCancelModify = useCallback(() => {
    console.log('[ZAXIA] Voice: Cancel modify');
    setVoiceCancelModify(true);
  }, []);

  // Phase D4.5.4: Slot completion handlers
  const handleVoiceSetModifyTime = useCallback((time: string) => {
    console.log('[ZAXIA] Voice: Set modify time', time);
    setPendingModifyChanges(prev => ({ ...prev, newTime: time }));
  }, []);

  const handleVoiceSetModifyDay = useCallback((day: string) => {
    console.log('[ZAXIA] Voice: Set modify day', day);
    setPendingModifyChanges(prev => ({ ...prev, newDay: day }));
  }, []);

  const handleVoiceSetModifyParty = useCallback((partySize: number) => {
    console.log('[ZAXIA] Voice: Set modify party size', partySize);
    setPendingModifyChanges(prev => ({ ...prev, newPartySize: partySize }));
  }, []);

  // Handler for when modify voice action has been processed by calendar
  const handleModifyVoiceActionHandled = useCallback(() => {
    setVoiceConfirmModify(false);
    setVoiceCancelModify(false);
    setIsModifyConfirmShowing(false);
  }, []);

  // Handler for when modify-confirm dialog is dismissed
  const handleModifyConfirmDismissed = useCallback(() => {
    console.log('[ZAXIA] Modify confirm dialog dismissed');
    setIsModifyConfirmShowing(false);
    setPendingModifyChanges(undefined);
    setPendingModifyBookingId(undefined);
  }, []);

  // Handler for when voice action has been processed by calendar
  const handleVoiceActionHandled = useCallback(() => {
    setVoiceConfirmCancel(false);
    setVoiceKeepBooking(false);
    setIsCancelConfirmShowing(false); // Dialog is now closed
  }, []);

  // Handler for when cancel-confirm dialog is dismissed (by button click or after successful cancel)
  const handleCancelConfirmDismissed = useCallback(() => {
    console.log('[ZAXIA] Cancel confirm dialog dismissed');
    setIsCancelConfirmShowing(false);
  }, []);

  const handleCalendarBack = useCallback(() => {
    console.log('[ZAXIA] Voice: Calendar back');
    setIsCancelConfirmShowing(false); // Clear any pending confirmation dialog
    setConciergeView('grid');
  }, []);

  const handleVoiceOpenBill = useCallback(() => {
    console.log('[ZAXIA] Voice: Open bill');
    setShowBill(true);
  }, []);

  // Phase D1.2: Voice: Open messages
  const handleVoiceOpenMessages = useCallback(() => {
    console.log('[ZAXIA] Voice: Open messages');
    setConciergeView('messages');
  }, []);

  const handleVoiceOpenWeather = useCallback(() => {
    console.log('[ZAXIA] Voice: Open weather');
    setConciergeView('weather');
  }, []);

  // Phase D3.1: Voice: Open explore
  const handleVoiceOpenExplore = useCallback(() => {
    console.log('[ZAXIA] Voice: Open explore');
    setConciergeView('explore');
  }, []);

  // Phase P2.2-10: Voice: Open green stay
  const handleVoiceOpenGreenStay = useCallback(() => {
    console.log('[ZAXIA] Voice: Open green stay');
    setConciergeView('green-stay');
  }, []);

  // Phase D4.5.1: Voice: Open room service
  const handleVoiceOpenRoomService = useCallback(() => {
    console.log('[ZAXIA] Voice: Open room service');
    setConciergeView('grid'); // First go to grid
    setVoiceOverlayActive(false); // Close overlay
    // Use timeout to ensure view is set before selecting service
    setTimeout(() => {
      handleServiceSelect('card_room_service');
    }, 100);
  }, [handleServiceSelect]);

  // Phase D4.5.1: Voice: Open spa
  const handleVoiceOpenSpa = useCallback(() => {
    console.log('[ZAXIA] Voice: Open spa');
    setConciergeView('grid'); // First go to grid
    setVoiceOverlayActive(false); // Close overlay
    // Use timeout to ensure view is set before selecting service
    setTimeout(() => {
      handleServiceSelect('card_spa');
    }, 100);
  }, [handleServiceSelect]);

  // P2.7-2: Voice: Open bar
  const handleVoiceOpenBar = useCallback(() => {
    console.log('[ZAXIA] Voice: Open bar');
    setServiceInitialOption(undefined); // clear any stale pre-selection
    setConciergeView('grid');
    setVoiceOverlayActive(false);
    setTimeout(() => { handleServiceSelect('card_bar'); }, 100);
  }, [handleServiceSelect]);

  // P2.7-2: Voice: Open pool
  const handleVoiceOpenPool = useCallback(() => {
    console.log('[ZAXIA] Voice: Open pool');
    setServiceInitialOption(undefined);
    setConciergeView('grid');
    setVoiceOverlayActive(false);
    setTimeout(() => { handleServiceSelect('card_pool'); }, 100);
  }, [handleServiceSelect]);

  // P2.7-2: Voice: Open gym
  const handleVoiceOpenGym = useCallback(() => {
    console.log('[ZAXIA] Voice: Open gym');
    setServiceInitialOption(undefined);
    setConciergeView('grid');
    setVoiceOverlayActive(false);
    setTimeout(() => { handleServiceSelect('card_gym'); }, 100);
  }, [handleServiceSelect]);

  // P2.7-2: Voice: Open transport service card
  const handleVoiceOpenTransport = useCallback(() => {
    console.log('[ZAXIA] Voice: Open transport');
    setServiceInitialOption(undefined);
    setConciergeView('grid');
    setVoiceOverlayActive(false);
    setTimeout(() => { handleServiceSelect('card_transport'); }, 100);
  }, [handleServiceSelect]);

  // P2.7-2: Voice: Book a specific Bar option
  const handleVoiceBookBar = useCallback((option?: string) => {
    console.log('[ZAXIA] Voice: Book bar option', option);
    setServiceInitialOption(option);
    setConciergeView('grid');
    setVoiceOverlayActive(false);
    setTimeout(() => { handleServiceSelect('card_bar'); }, 100);
  }, [handleServiceSelect]);

  // P2.7-2: Voice: Book a specific Pool option
  const handleVoiceBookPool = useCallback((option?: string) => {
    console.log('[ZAXIA] Voice: Book pool option', option);
    setServiceInitialOption(option);
    setConciergeView('grid');
    setVoiceOverlayActive(false);
    setTimeout(() => { handleServiceSelect('card_pool'); }, 100);
  }, [handleServiceSelect]);

  // P2.7-2: Voice: Book a specific Gym option
  const handleVoiceBookGym = useCallback((option?: string) => {
    console.log('[ZAXIA] Voice: Book gym option', option);
    setServiceInitialOption(option);
    setConciergeView('grid');
    setVoiceOverlayActive(false);
    setTimeout(() => { handleServiceSelect('card_gym'); }, 100);
  }, [handleServiceSelect]);

  // P2.7-2: Voice: Book a specific Transport option
  const handleVoiceBookTransport = useCallback((option?: string) => {
    console.log('[ZAXIA] Voice: Book transport option', option);
    setServiceInitialOption(option);
    setConciergeView('grid');
    setVoiceOverlayActive(false);
    setTimeout(() => { handleServiceSelect('card_transport'); }, 100);
  }, [handleServiceSelect]);

  // P2.7-2: Booking modal voice command handlers
  const handleBMSetDate = useCallback((dateText: string) => {
    if (!bookingService) return;
    setBookingModalVoiceCmd({ type: 'SET_DATE', payload: { dateText }, id: ++bookingModalVoiceCmdIdRef.current });
  }, [bookingService]);

  const handleBMSetTime = useCallback((timeText: string) => {
    if (!bookingService) return;
    setBookingModalVoiceCmd({ type: 'SET_TIME', payload: { timeText }, id: ++bookingModalVoiceCmdIdRef.current });
  }, [bookingService]);

  const handleBMConfirm = useCallback(() => {
    if (!bookingService) return;
    setBookingModalVoiceCmd({ type: 'CONFIRM', id: ++bookingModalVoiceCmdIdRef.current });
  }, [bookingService]);

  const handleBMCancel = useCallback(() => {
    if (!bookingService) return;
    setBookingModalVoiceCmd({ type: 'CANCEL', id: ++bookingModalVoiceCmdIdRef.current });
  }, [bookingService]);

  // Phase D4.5.2: Voice: Open restaurant with initial slots
  const handleVoiceOpenRestaurant = useCallback((slots?: { time?: string; partySize?: number; transcript?: string }) => {
    console.log('[ZAXIA] Voice: Open restaurant', slots);
    setConciergeView('grid'); // First go to grid
    setVoiceOverlayActive(false); // Close overlay
    // Store initial slots before navigating to restaurant
    setRestaurantInitialSlots(slots);
    // Use timeout to ensure view is set before selecting service
    setTimeout(() => {
      handleServiceSelect('card_restaurant');
    }, 100);
  }, [handleServiceSelect]);

  // Phase D1.2: Voice: Home (return to grid)
  const handleVoiceHome = useCallback(() => {
    console.log('[ZAXIA] Voice: Home');
    setConciergeView('grid');
    setShowBill(false);
    setSelectedBooking(null);
  }, []);

  // Phase D1.2: Track current TV channel index for next/prev
  const [currentChannelIndex, setCurrentChannelIndex] = useState(0);

  const handleVoiceTvChannel = useCallback((channel: string) => {
    console.log('[ZAXIA] Voice: TV channel', channel);
    // Map channel name to index
    const channelMap: Record<string, number> = {
      'hbo': 0, 'cnn': 1, 'bbc': 2, 'espn': 3, 'discovery': 4, 'streaming': 5
    };
    const index = channelMap[channel.toLowerCase()];
    if (index !== undefined) {
      setCurrentChannelIndex(index);
    }
  }, []);

  // Phase D1.2: Next/Previous channel
  const handleVoiceTvNextChannel = useCallback(() => {
    console.log('[ZAXIA] Voice: Next channel');
    setCurrentChannelIndex(prev => (prev + 1) % 6); // 6 channels
  }, []);

  const handleVoiceTvPrevChannel = useCallback(() => {
    console.log('[ZAXIA] Voice: Previous channel');
    setCurrentChannelIndex(prev => (prev - 1 + 6) % 6); // 6 channels
  }, []);

  const handleVoiceTvFullscreen = useCallback(() => {
    console.log('[ZAXIA] Voice: TV fullscreen');
    setTvFullscreen(true);
  }, []);

  // Phase D1.2: Exit TV fullscreen
  const handleVoiceTvExitFullscreen = useCallback(() => {
    console.log('[ZAXIA] Voice: Exit TV fullscreen');
    setTvFullscreen(false);
  }, []);

  // Phase D2.2: Mute/Unmute TV
  const handleVoiceTvMute = useCallback(() => {
    console.log('[ZAXIA] Voice: Mute TV');
    setTvMuted(true);
  }, []);

  const handleVoiceTvUnmute = useCallback(() => {
    console.log('[ZAXIA] Voice: Unmute TV');
    setTvMuted(false);
  }, []);

  // Phase D2.2: TV ON/OFF
  const handleVoiceTvOn = useCallback(() => {
    console.log('[ZAXIA] Voice: TV ON');
    setIsTvOn(true);
  }, []);

  const handleVoiceTvOff = useCallback(() => {
    console.log('[ZAXIA] Voice: TV OFF');
    setIsTvOn(false);
  }, []);

  // Phase 4A: Voice booking persistence
  const handleVoiceBookingCreated = useCallback(async (booking: CreateBookingRequest) => {
    console.log('[ZAXIA] Voice booking created:', booking);
    try {
      await addBooking(booking);
      console.log('[ZAXIA] Voice booking persisted to backend');
      await refreshBill();
    } catch (e) {
      console.error('[ZAXIA] Failed to persist voice booking:', e);
      throw e; // Re-throw so AskZaxiaView knows it failed
    }
  }, [addBooking, refreshBill]);

  // Phase D4.1 + D4.2: Voice enable Green Stay
  const handleVoiceGreenStayEnable = useCallback(() => {
    console.log('[ZAXIA] Voice: Enable Green Stay');
    setGreenStayEnabled(true);
    persistGreenPreferences({ greenStayOptIn: true });
    setGreenStayToastMessage('Green Stay enabled');
    setShowGreenStayToast(true);
    setTimeout(() => setShowGreenStayToast(false), 4000);
  }, [persistGreenPreferences]);

  // Phase P2.2-10: Helper to filter out future dates (keep only past dates)
  const filterPastDates = useCallback((dates: string[]): string[] => {
    const today = new Date().toISOString().split('T')[0]!;
    return dates.filter(date => date < today);
  }, []);

  // Phase D4.1 + D4.2: Voice disable Green Stay (arrays cleared by AskZaxiaView)
  const handleVoiceGreenStayDisable = useCallback(() => {
    console.log('[ZAXIA] Voice: Disable Green Stay');
    setGreenStayEnabled(false);
    // Phase P2.2-10: Keep past dates, clear future dates only
    const pastSkipDays = filterPastDates(skipCleaningDays);
    const pastTowelsDays = filterPastDates(towelsOnlyDays);
    setSkipCleaningDays(pastSkipDays);
    setTowelsOnlyDays(pastTowelsDays);
    persistGreenPreferences({ greenStayOptIn: false, skipCleaningDates: pastSkipDays, towelsOnlyDates: pastTowelsDays });
  }, [persistGreenPreferences, filterPastDates, skipCleaningDays, towelsOnlyDays]);

  // Phase D4.2: Handle toggle from UI - clear arrays when disabling
  const handleGreenStayToggle = useCallback(() => {
    const newValue = !greenStayEnabled;
    setGreenStayEnabled(newValue);
    if (newValue) {
      // Enabling - just set opt-in
      persistGreenPreferences({ greenStayOptIn: true });
      setGreenStayToastMessage('Green Stay enabled');
      setShowGreenStayToast(true);
      setTimeout(() => setShowGreenStayToast(false), 4000);
      // Note: Logging handled by GreenStayView on save
    } else {
      // Disabling - clear future scheduled preferences, keep past dates
      const pastSkipDays = filterPastDates(skipCleaningDays);
      const pastTowelsDays = filterPastDates(towelsOnlyDays);
      setSkipCleaningDays(pastSkipDays);
      setTowelsOnlyDays(pastTowelsDays);
      persistGreenPreferences({ greenStayOptIn: false, skipCleaningDates: pastSkipDays, towelsOnlyDates: pastTowelsDays });
      setGreenStayToastMessage('Green Stay disabled - future preferences cleared');
      setShowGreenStayToast(true);
      setTimeout(() => setShowGreenStayToast(false), 4000);
      // Note: Logging handled by GreenStayView on save
    }
  }, [greenStayEnabled, persistGreenPreferences, filterPastDates, skipCleaningDays, towelsOnlyDays]);

  // Phase P2.2-10: Simple enable handler for GreenStayView
  const handleGreenStayEnable = useCallback(() => {
    console.log('[ZAXIA] Enable Green Stay from view');
    setGreenStayEnabled(true);
    persistGreenPreferences({ greenStayOptIn: true });
    setGreenStayToastMessage('Green Stay enabled');
    setShowGreenStayToast(true);
    setTimeout(() => setShowGreenStayToast(false), 4000);
    // Note: Logging handled by GreenStayView on save
  }, [persistGreenPreferences]);

  // Phase P2.2-10: Simple disable handler for GreenStayView
  const handleGreenStayDisable = useCallback(() => {
    console.log('[ZAXIA] Disable Green Stay from view');
    setGreenStayEnabled(false);
    // Phase P2.2-10: Keep past dates, clear future dates only
    const pastSkipDays = filterPastDates(skipCleaningDays);
    const pastTowelsDays = filterPastDates(towelsOnlyDays);
    setSkipCleaningDays(pastSkipDays);
    setTowelsOnlyDays(pastTowelsDays);
    persistGreenPreferences({ greenStayOptIn: false, skipCleaningDates: pastSkipDays, towelsOnlyDates: pastTowelsDays });
    setGreenStayToastMessage('Green Stay disabled - future preferences cleared');
    setShowGreenStayToast(true);
    setTimeout(() => setShowGreenStayToast(false), 4000);
    // Note: Logging handled by GreenStayView on save
  }, [persistGreenPreferences, filterPastDates, skipCleaningDays, towelsOnlyDays]);

  // Phase D4.2: Handle skip cleaning days change
  // Phase P2.2-10: Eco logging moved to GreenStayView save action
  const handleSkipCleaningChange = useCallback((days: string[], opts?: { silent?: boolean }) => {
    setSkipCleaningDays(days);
    persistGreenPreferences({ skipCleaningDates: days });

    // When called from GreenStayView save, suppress toast/savings (view handles its own feedback)
    if (!opts?.silent) {
      setGreenStayToastMessage('Green Stay preferences updated');
      setShowGreenStayToast(true);
      setTimeout(() => setShowGreenStayToast(false), 4000);

      // Show savings feedback if days were added
      if (days.length > skipCleaningDays.length) {
        const savings = calculateStaySavings(days, towelsOnlyDays);
        setCurrentSavings(savings);
        setShowSavingsFeedback(true);
        setTimeout(() => setShowSavingsFeedback(false), 6000);
      }
    }
  }, [persistGreenPreferences, skipCleaningDays, towelsOnlyDays]);

  // Phase D4.2: Handle towels only days change
  // Phase P2.2-10: Eco logging moved to GreenStayView save action
  const handleTowelsOnlyChange = useCallback((days: string[], opts?: { silent?: boolean }) => {
    setTowelsOnlyDays(days);
    persistGreenPreferences({ towelsOnlyDates: days });

    // When called from GreenStayView save, suppress toast/savings (view handles its own feedback)
    if (!opts?.silent) {
      setGreenStayToastMessage('Green Stay preferences updated');
      setShowGreenStayToast(true);
      setTimeout(() => setShowGreenStayToast(false), 4000);

      // Show savings feedback if days were added
      if (days.length > towelsOnlyDays.length) {
        const savings = calculateStaySavings(skipCleaningDays, days);
        setCurrentSavings(savings);
        setShowSavingsFeedback(true);
        setTimeout(() => setShowSavingsFeedback(false), 6000);
      }
    }
  }, [persistGreenPreferences, towelsOnlyDays, skipCleaningDays]);

  // ==========================================================================
  // Phase D4.5.5: Demo Mode Handlers
  // ==========================================================================

  // Reset demo - clear all bookings and return to home, replay arrival
  const handleDemoReset = useCallback(async () => {
    console.log('[ZAXIA Demo] Resetting demo...');
    await api.resetDemo();
    await refreshBookings();
    await refreshBill();
    setConciergeView('grid');
    setSelectedBooking(null);
    setSelectedService(null);
    // Phase D5.1: Replay arrival experience on demo reset
    setAppFlow('arrival');
    console.log('[ZAXIA Demo] Demo reset complete, replaying arrival');
  }, [refreshBookings, refreshBill]);

  // Restart session - full reload
  const handleDemoRestartSession = useCallback(async () => {
    console.log('[ZAXIA Demo] Restarting session...');
    await api.restartSession();
    // Force reload after brief delay
    setTimeout(() => window.location.reload(), 500);
  }, []);

  // Clear all bookings only
  const handleDemoClearBookings = useCallback(async () => {
    console.log('[ZAXIA Demo] Clearing all bookings...');
    await api.clearAllBookings();
    await refreshBookings();
    await refreshBill();
    console.log('[ZAXIA Demo] Bookings cleared');
  }, [refreshBookings, refreshBill]);

  // Show loading state
  if (loading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center gradient-dark">
        <div className="text-white text-xl">Loading ZAXIA...</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="w-screen h-screen flex items-center justify-center gradient-dark">
        <div className="text-center">
          <div className="text-red-400 text-xl mb-2">Connection Error</div>
          <div className="text-white/60 text-sm">
            Could not connect to backend. Make sure the server is running on port 3001.
          </div>
        </div>
      </div>
    );
  }

  // PILOT-3: Show consent gate before anything else (once data is loaded)
  if (!consented && stay && room && hotel) {
    return (
      <GuestConsentBanner
        stayId={stay.stayId}
        roomNumber={room.roomNumber}
        hotelName={hotel.name}
        onConsent={() => setConsented(true)}
      />
    );
  }

  // Phase D5.1: Show Arrival Experience (Flow 0) before main app
  if (appFlow === 'arrival' && guest && hotel) {
    return (
      <ArrivalExperience
        guestFirstName={guest.firstName}
        hotelName={hotel.name}
        onComplete={handleArrivalComplete}
      />
    );
  }

  return (
    <div className="w-screen h-screen overflow-hidden gradient-dark">
      {/* Phase 2B: Booking Confirmation Modal */}
      {bookingService && bookingOption && stay && (
        <BookingConfirmationModal
          isOpen={true}
          onCancel={handleBookingCancel}
          onConfirm={handleBookingConfirm}
          service={bookingService}
          option={bookingOption}
          stayId={stay.stayId}
          voiceCmd={bookingModalVoiceCmd}
          onVoiceCmdHandled={() => setBookingModalVoiceCmd(null)}
        />
      )}

      {/* Phase 2B: Booking Detail Modal */}
      <BookingDetailModal
        isOpen={selectedBooking !== null}
        onClose={() => setSelectedBooking(null)}
        booking={selectedBooking}
      />

      {/* Phase 2B: Hotel Bill Modal - now using backend data */}
      <HotelBillModal
        isOpen={showBill}
        onClose={() => setShowBill(false)}
        bill={bill ?? undefined}
      />

      {/* Main Layout: 3 columns — tv-safe-area adds overscan padding on TV viewports */}
      {/* P2.8-3d: zaxia-3col-grid + data-tv-mode lets CSS target all 3 columns for TV density */}
      <div
        className={`zaxia-3col-grid h-full grid grid-cols-[1fr_4fr_1.5fr]${isTV ? ' tv-safe-area' : ''}`}
        {...(isTV ? { 'data-tv-mode': 'true' } : {})}
      >
        {/* Left Sidebar (1/8 equivalent) */}
        <UsefulInfoSidebar
          enabled={isZoneActive('sidebar')}
          greenStayEnabled={greenStayEnabled}
          skipCleaningDays={skipCleaningDays}
          towelsOnlyDays={towelsOnlyDays}
          stayCheckIn={stay?.checkInDate ?? new Date().toISOString()}
          stayCheckOut={stay?.checkOutDate ?? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()}
          showGreenStayToast={showGreenStayToast}
          greenStayToastMessage={greenStayToastMessage}
          onGreenStayToggle={handleGreenStayToggle}
          onSkipCleaningChange={handleSkipCleaningChange}
          onTowelsOnlyChange={handleTowelsOnlyChange}
          onCalendarClick={handleCalendarClick}
          onMessagesClick={handleMessagesClick}
          onRequestsClick={handleRequestsClick}
          onWeatherClick={handleWeatherClick}
          onGreenStayClick={handleGreenStayClick}
          viewIsOpen={conciergeView !== 'grid'}
          bookings={bookings}
          onRightBoundary={handleSidebarRightBoundary}
        />

        {/* Center Concierge Area - service detail and placeholder views render inside this */}
        {/* Phase D4.5.1: Voice callbacks moved to GlobalVoiceOverlay - ConciergeGrid simplified */}
        {/* P2.8-3b: TVSafeAreaLayout wraps the centre column only — sidebar and TV pane unaffected */}
        <TVSafeAreaLayout className="overflow-hidden">
        <main className="h-full overflow-hidden">
          <ConciergeGrid
            enabled={isZoneActive('main')}
            onServiceSelect={handleServiceSelect}
            activeZone={activeZone}
            selectedService={selectedService}
            onServiceBack={handleServiceBack}
            onOptionSelect={handleOptionSelect}
            activeView={conciergeView}
            onViewClose={handleViewClose}
            bookings={bookings}
            calendarEvents={calendarEvents}
            onSelectBooking={handleBookingSelect}
            onSelectEvent={(event) => console.log('[ZAXIA] Event selected:', event.title)}
            viewIsOpen={conciergeView !== 'grid'}
            skipCleaningDays={skipCleaningDays}
            towelsOnlyDays={towelsOnlyDays}
            greenStayEnabled={greenStayEnabled}
            onGreenStayEnable={handleGreenStayEnable}
            onGreenStayDisable={handleGreenStayDisable}
            onSkipCleaningChange={handleSkipCleaningChange}
            onTowelsOnlyChange={handleTowelsOnlyChange}
            roomNumber={room?.roomNumber || ''}
            stayId={stay?.stayId || ''}
            onVoiceBookingCreated={handleVoiceBookingCreated}
            restaurantInitialSlots={restaurantInitialSlots}
            serviceInitialOption={serviceInitialOption}
            stayCheckIn={stay?.checkInDate ?? new Date().toISOString()}
            stayCheckOut={stay?.checkOutDate ?? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()}
            // Phase D4.5.3: Calendar voice navigation
            calendarInitialDate={calendarInitialDate}
            // Phase D4.5.3: Booking cancellation - refresh data after cancel
            onBookingCancelled={() => { refreshBookings(); refreshBill(); }}
            // Phase D4.5.3: Voice-initiated cancellation
            pendingCancelBookingId={pendingCancelBookingId}
            onPendingCancelHandled={handlePendingCancelHandled}
            // Voice-initiated selection
            pendingSelectBookingId={pendingSelectBookingId}
            onPendingSelectHandled={handlePendingSelectHandled}
            // Phase D4.5.3: Voice confirmation of cancellation
            voiceConfirmCancel={voiceConfirmCancel}
            voiceKeepBooking={voiceKeepBooking}
            onVoiceActionHandled={handleVoiceActionHandled}
            onCancelConfirmDismissed={handleCancelConfirmDismissed}
            // Phase D4.5.4: Voice-initiated modification
            pendingModifyBookingId={pendingModifyBookingId}
            pendingModifyChanges={pendingModifyChanges}
            onPendingModifyHandled={handlePendingModifyHandled}
            voiceConfirmModify={voiceConfirmModify}
            voiceCancelModify={voiceCancelModify}
            onModifyVoiceActionHandled={handleModifyVoiceActionHandled}
            onModifyConfirmDismissed={handleModifyConfirmDismissed}
            onBookingModified={() => { refreshBookings(); refreshBill(); }}
            voiceBookingNavTrigger={calendarVoiceBookingNav}
            onVoiceBookingNavHandled={handleVoiceBookingNavHandled}
            voiceDayNavTrigger={calendarVoiceDayNav}
            onVoiceDayNavHandled={handleVoiceDayNavHandled}
            // Phase P2.2-9: Requests voice command handler registration
            onRequestsVoiceCommandReady={handleRequestsVoiceCommandReady}
            onGreenStayVoiceCommandReady={handleGreenStayVoiceCommandReady}
            onGridLeftBoundary={handleMainGridLeftBoundary}
            onGridRightBoundary={handleMainGridRightBoundary}
            onMessagesVoiceCommandReady={handleMessagesVoiceCommandReady}
          />
        </main>
        </TVSafeAreaLayout>

        {/* Right TV Section (1/4 equivalent) */}
        <TVSection
          enabled={isZoneActive('tv')}
          onBillClick={handleBillClick}
          onChannelSelect={handleChannelSelect}
          tvFullscreen={tvFullscreen}
          onFullscreenToggle={() => setTvFullscreen(!tvFullscreen)}
          currentChannelIndex={currentChannelIndex}
          isMuted={tvMuted}
          onMuteToggle={() => setTvMuted(!tvMuted)}
          isTvOn={isTvOn}
          onPowerToggle={() => setIsTvOn(!isTvOn)}
          guest={guest}
          room={room}
          stay={stay}
        />
      </div>

      {/* Phase D2.2: TV Fullscreen Overlay - only show when TV is ON */}
      {isTvOn && (
        <TVFullscreenOverlay
          isOpen={tvFullscreen}
          onClose={() => setTvFullscreen(false)}
          currentChannelIndex={currentChannelIndex}
          onNextChannel={handleVoiceTvNextChannel}
          onPrevChannel={handleVoiceTvPrevChannel}
          isMuted={tvMuted}
          onMuteToggle={() => setTvMuted(!tvMuted)}
        />
      )}

      {/* Phase D4.5.1: Global Voice Overlay */}
      <GlobalVoiceOverlay
        isActive={voiceOverlayActive}
        onClose={() => setVoiceOverlayActive(false)}
        guest={guest}
        currentView={conciergeView as any}
        onOpenCalendar={handleVoiceOpenCalendar}
        onOpenCalendarDay={handleVoiceOpenCalendarDay}
        onOpenBill={handleVoiceOpenBill}
        onOpenMessages={handleVoiceOpenMessages}
        onOpenWeather={handleVoiceOpenWeather}
        onHome={handleVoiceHome}
        // Phase D4.5.3: Calendar control callbacks
        onCalendarNextDay={handleCalendarNextDay}
        onCalendarPrevDay={handleCalendarPrevDay}
        onCalendarGoToDay={handleCalendarGoToDay}
        onCalendarCancelBooking={handleCalendarCancelBooking}
        onCalendarSelectBooking={handleCalendarSelectBooking}
        onCalendarNextBooking={handleCalendarNextBooking}
        onCalendarPrevBooking={handleCalendarPrevBooking}
        onCalendarBack={handleCalendarBack}
        bookings={bookings}
        // Phase D4.5.3: Voice confirmation - track when cancel dialog is showing
        isCancelConfirmShowing={isCancelConfirmShowing}
        // Phase D4.5.4: Modify booking callbacks
        onCalendarModifyBooking={handleCalendarModifyBooking}
        onCalendarConfirmModify={handleVoiceConfirmModify}
        onCalendarCancelModify={handleVoiceCancelModify}
        onCalendarSetModifyTime={handleVoiceSetModifyTime}
        onCalendarSetModifyDay={handleVoiceSetModifyDay}
        onCalendarSetModifyParty={handleVoiceSetModifyParty}
        isModifyConfirmShowing={isModifyConfirmShowing}
        onSwitchToSidebar={handleSwitchToSidebar}
        onSwitchToConcierge={handleSwitchToConcierge}
        onSwitchToTv={handleSwitchToTv}
        onTvChannel={handleVoiceTvChannel}
        onTvNextChannel={handleVoiceTvNextChannel}
        onTvPrevChannel={handleVoiceTvPrevChannel}
        onTvFullscreen={handleVoiceTvFullscreen}
        onTvExitFullscreen={handleVoiceTvExitFullscreen}
        onTvMute={handleVoiceTvMute}
        onTvUnmute={handleVoiceTvUnmute}
        onTvOn={handleVoiceTvOn}
        onTvOff={handleVoiceTvOff}
        isTvFullscreen={tvFullscreen}
        onOpenRoomService={handleVoiceOpenRoomService}
        onOpenSpa={handleVoiceOpenSpa}
        onOpenExplore={handleVoiceOpenExplore}
        onOpenBar={handleVoiceOpenBar}
        onOpenPool={handleVoiceOpenPool}
        onOpenGym={handleVoiceOpenGym}
        onOpenTransport={handleVoiceOpenTransport}
        onBookBar={handleVoiceBookBar}
        onBookPool={handleVoiceBookPool}
        onBookGym={handleVoiceBookGym}
        onBookTransport={handleVoiceBookTransport}
        bookingModalOpen={!!(bookingService && bookingOption)}
        onBookingModalSetDate={handleBMSetDate}
        onBookingModalSetTime={handleBMSetTime}
        onBookingModalConfirm={handleBMConfirm}
        onBookingModalCancel={handleBMCancel}
        onOpenRequests={handleRequestsClick}
        onOpenGreenStay={handleVoiceOpenGreenStay}
        onRequestsVoiceCommand={requestsVoiceCommandHandler || undefined}
        onGreenStayVoiceCommand={greenStayVoiceCommandHandler || undefined}
        onMessagesVoiceCommand={messagesVoiceCommandHandler || undefined}
        onOpenRestaurant={handleVoiceOpenRestaurant}
        onBookingCreated={handleVoiceBookingCreated}
        greenStayEnabled={greenStayEnabled}
        onGreenStayEnable={handleVoiceGreenStayEnable}
        onGreenStayDisable={handleVoiceGreenStayDisable}
        skipCleaningDays={skipCleaningDays}
        towelsOnlyDays={towelsOnlyDays}
        stayCheckIn={stay?.checkInDate ?? new Date().toISOString()}
        stayCheckOut={stay?.checkOutDate ?? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()}
        onSkipCleaningChange={handleSkipCleaningChange}
        onTowelsOnlyChange={handleTowelsOnlyChange}
        // Phase D4.5.3: Voice confirmation of cancellation
        onCalendarConfirmCancel={handleVoiceConfirmCancel}
        onCalendarKeepBooking={handleVoiceKeepBooking}
        // Phase D4.5.5: Demo mode voice commands
        isDemoMode={isDemoMode}
        onDemoReset={handleDemoReset}
        onDemoRestartSession={handleDemoRestartSession}
        onDemoClearBookings={handleDemoClearBookings}
      />

      {/* Phase P2.2-6: Guest request status notifications */}
      <RequestStatusNotification
        message={notification?.message || null}
        type={notification?.type || null}
        onDismiss={dismissNotification}
      />

      {/* Phase P2.2-10: Eco savings feedback overlay */}
      <GreenStaySavingsFeedback
        savings={currentSavings}
        visible={showSavingsFeedback}
        onClose={() => setShowSavingsFeedback(false)}
      />

      {/* Phase D4.5.5: Demo mode indicator */}
      <DemoModeIndicator />

      {/* Debug Info (dev only) */}
      {import.meta.env.DEV && hotel && guest && room && (
        <div className="fixed bottom-2 left-2 text-xs text-zaxia-gray/30 font-mono">
          <div>Hotel: {hotel.name}</div>
          <div>Guest: {guest.firstName} {guest.lastName}</div>
          <div>Room: {room.roomNumber}</div>
          <div>Zone: {activeZone}</div>
          <div>Bookings: {bookings.length}</div>
          {isDemoMode && <div className="text-amber-400">DEMO MODE</div>}
        </div>
      )}
    </div>
  );
}
